import { useEffect, useRef, useState } from 'react';

import { DatePickerComponent, MaskedDateTime } from '@syncfusion/ej2-react-calendars';
import {
    MultiSelectComponent,
    Inject,
    CheckBoxSelection,
    DropDownListComponent,
} from '@syncfusion/ej2-react-dropdowns';
import { AxiosError } from 'axios';
import omit from 'lodash/omit';
import moment from 'moment/moment';
import { Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { requests } from 'components/AvaEzm/AxiosApi';
import { IpaCounty } from 'components/ProviderData/IpaAdministration/atoms/Models';
import { ipaPlanService } from 'components/ProviderData/subjects/IpaPlanService';
import DisabledWrapper from 'components/ProviderData/utils/DisabledWrapper';
import { dialogService } from 'subjects/common/DialogService';

export const IpaCountyMappingForm = ({
    IPAPlanData,
    ipaCounty,
    companyCode,
    ipaCode,
    ipaId,
    isViewer,
}) => {
    const [startDate, setStartDate] = useState<Date>();
    const [overlapError] = useState<string>();
    const [operatingCountiesData, setOperatingCountiesData] = useState<any[]>([]);
    const [companyCodes, setCompanyCodes] = useState<any[]>([]);
    const [contiguousCounties, setContiguousCounties] = useState<any[]>([]);
    const operatingCountiesListRef = useRef<any[]>([]);
    const countiguousCountiesListRef = useRef<any[]>([]);
    const {
        register,
        handleSubmit,
        setValue,
        trigger,
        getValues,
        reset,
        formState: { errors },
    } = useForm<any>({
        defaultValues: {
            isActive: true,
        },
    });
    const fetchOperatingCounties = async () => {
        const year = moment(ipaCounty?.effectiveBeginDate).format('YYYY');
        const _opCounties: any[] = await requests.get(`/api/IpaCountyMapping/GetOperatingCounties?IpaCode=${ipaCode}&planYear=${year}`,)
        operatingCountiesListRef.current = _opCounties;
        setOperatingCountiesData(filterCountyByCompanyCode(_opCounties))
    };

    const fetchCompanyCodes = async () => {
        const _companyCodes: any[] = await requests.get('/api/MedicalGroup/GetCompanyCodes');
        setCompanyCodes(_companyCodes);
    };
    const filterCountyByCompanyCode = (_opCounties: any[]) => {
        const _companyCode = getValues('companyCode') ?? companyCode;
        const filteredCounties = _opCounties.filter((obj) => {
            if (_companyCode === 'CCHP') {
                return obj.companyCode === 'AHP'
            } else return obj.companyCode === getValues('companyCode')
        });
        return filteredCounties;
    };

    const fetchContiguousCounties = async () => {
        const _counties: any[] = await requests.get(`/api/IpaCountyMapping/GetContiguousCounties`);
        setContiguousCounties(_counties);
        countiguousCountiesListRef.current = _counties;
    };

    const validateCountySelection = () => {
        const selectedContiguousCounties = getValues('contiguousCounties') || [];
        const selectedOperatingCounties = getValues('operatingCounties') || [];

        const commonCounties = selectedOperatingCounties.filter((county) =>
            selectedContiguousCounties.includes(county),
        );

        if (commonCounties.length > 0) {
            return 'A county cannot be selected as both Operating and Contiguous.';
        }
    };

    const saveInformation = async (data: IpaCounty) => {
        handleDateValid(data.effectiveBeginDate);
        try {
            dialogService.loading(true);
            const _operatingCounties = Array.isArray(data.operatingCounties)
                ? operatingCountiesListRef.current?.filter((o) => {
                    if (data.operatingCounties?.includes(o.countyStateCode)) {
                        if (o.companyCode === getValues('companyCode') || o.companyCode === 'AHP') {
                            return o;
                        }
                    }
                })
                : [];
            const _contiguousCounties = Array.isArray(data.contiguousCounties)
                ? countiguousCountiesListRef?.current?.filter((o) =>
                    data.contiguousCounties?.includes(o.countyStateCode))
                : [];
            const contractStartDate = moment(data.effectiveBeginDate).format('MM/DD/YYYY');
            const contractEndDate = moment(data.effectiveEndDate).format('MM/DD/YYYY');
            const adaptedData = omit(
                {
                    ...data,
                    operatingCounties: _operatingCounties,
                    contiguousCounties: _contiguousCounties,
                    effectiveBeginDate: contractStartDate,
                    effectiveEndDate: contractEndDate,
                },
                ['isDeleted'],
            );

            const url = ipaCounty?.id ? `/api/IpaCountyMapping/${ipaCounty?.id}` : `/api/IpaCountyMapping/Create`;
            if (ipaCounty?.id) {
                await requests.put(url, { data: adaptedData });
            } else {
                await requests.post(url, { data: adaptedData });
            }

            dialogService.loading(false);
            dialogService.close();
            ipaPlanService.submit({ error: false });
        } catch (e) {
            const message = `${(e as AxiosError)?.response?.data}`;
            ipaPlanService.submit({ error: true, message });
            dialogService.loading(false);
        }
    };

    useEffect(() => {
        const subscription = ipaPlanService.submit$().subscribe((validated) => {
            if (validated === undefined) {
                handleSubmit(saveInformation)();
            }
        });
        if (ipaCounty.id) {
            for (const c in ipaCounty) {
                if (Array.isArray(ipaCounty[c])) {
                    if (ipaCounty[c]) {
                        setValue(
                            c,
                            ipaCounty[c].map((v) => v.countyStateCode),
                        );
                    }
                } else {
                    setValue(c, ipaCounty[c]);
                }
            }
            setValue('startEndDateExist', false);
        } else {
            reset();
            setValue('ipaCode', ipaCode);
        }
        setValue('companyCode', companyCode);
        setValue('ipaId', ipaId);
        return () => {
            subscription.unsubscribe();
        };
    }, [ipaCounty]);

    useEffect(() => {
        fetchOperatingCounties();
        fetchCompanyCodes();
        fetchContiguousCounties();
    }, []);

    const handleDateValid = (value) => {
        const startDate = moment(getValues('effectiveBeginDate')).format('MM DD YYYY');
        const endDate = moment(value).format('MM DD YYYY');
        const match = IPAPlanData.some((item) => {
            return (
                (moment(item.effectiveBeginDate).format('MM DD YYYY') === startDate &&
                    moment(item?.effectiveEndDate).format('MM DD YYYY') === endDate) ||
                new Date(startDate) <= new Date(moment(item?.effectiveEndDate).format('MM DD YYYY'))
            );
        });
        if (!ipaCounty?.id) {
            setValue('startEndDateExist', match);
        }
    };

    return (
        <Row>
            <DisabledWrapper disabled={isViewer}>
                <form
                    className='ipa-tab-form'
                    style={{ padding: '5px' }}
                >
                    <div className='row'>
                        {ipaCounty?.id && (
                            <div className='col-md-4'>
                                <div className='text-value'>
                                    <label className='float-input-field'>IPA ID</label>
                                    <label className='float-input-value'>{ipaId}</label>
                                    <span className='e-float-line' />
                                </div>
                            </div>
                        )}
                        <div className='col-md-4'>
                            <div className='text-value'>
                                <label className='float-input-field'>IPA Code</label>
                                <label className='float-input-value'>{ipaCode}</label>
                                <span className='e-float-line' />
                            </div>
                        </div>
                    </div>

                    <div className={'row'}>
                        <div className='col-md-6 mt-3'>
                            <div>
                                <label className='e-label-select mb-2'>Start Date *</label>
                                <DatePickerComponent
                                    format='MM/dd/yyyy'
                                    enableMask={true}
                                    id='calendar_start'
                                    {...register('effectiveBeginDate', { required: 'Start Date is required' })}
                                    change={(e) => {
                                        if (e.isInteracted) {
                                            const startOfMonth = moment(e.value).startOf('month').format();
                                            setValue('effectiveBeginDate', startOfMonth);
                                            setValue('effectiveEndDate', null);
                                            setStartDate(e.value);
                                        }
                                    }}
                                    min={undefined}
                                    max={new Date(9999, 11, 31)}
                                    strictMode
                                    start='Year'
                                    depth='Year'
                                    value={
                                        getValues('effectiveBeginDate') ? getValues('effectiveBeginDate') : undefined
                                    }
                                >
                                    <Inject services={[MaskedDateTime]} />
                                </DatePickerComponent>
                            </div>
                            {errors.effectiveBeginDate && (
                                <p className='error-form'>{errors.effectiveBeginDate.message}</p>
                            )}
                        </div>

                        <div className='col-md-6 mt-3'>
                            <div>
                                <label className='e-label-select mb-2'>End Date *</label>
                                <DatePickerComponent
                                    format='MM/dd/yyyy'
                                    enableMask={true}
                                    id='calendar_end'
                                    {...register('effectiveEndDate', {
                                        required: 'End Date is required',
                                        validate: () =>
                                            getValues('startEndDateExist') === false ||
                                            'Already Exist, Please select different Start Date and End Date',
                                    })}
                                    change={(e) => {
                                        if (e.isInteracted) {
                                            const endOfMonth = moment(e.value).endOf('month').startOf('day').format();
                                            setValue('effectiveEndDate', endOfMonth);
                                            handleDateValid(e.value);
                                            trigger('effectiveEndDate');
                                        }
                                    }}
                                    start='Year'
                                    depth='Year'
                                    min={startDate}
                                    max={new Date(9999, 11, 31)}
                                    strictMode
                                >
                                    <Inject services={[MaskedDateTime]} />
                                </DatePickerComponent>
                            </div>
                            {errors.effectiveEndDate && <p className='error-form'>{errors.effectiveEndDate.message}</p>}
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-md-6 mt-3'>
                            <div>
                                <label className='e-label-select'>Company Code *</label>
                                <DropDownListComponent
                                    {...register('companyCode', {
                                        required: 'Company Code is required',
                                    })}
                                    dataSource={companyCodes}
                                    fields={{
                                        value: 'companyCode',
                                        text: 'companyName',
                                    }}
                                    placeholder='Select a Company Code'
                                    popupHeight='220px'
                                    change={(e) => {
                                        if (e.isInteracted) {
                                            setValue('companyCode', e.value);
                                            setValue('operatingCounties', []);
                                            setOperatingCountiesData(
                                                filterCountyByCompanyCode(operatingCountiesListRef?.current))
                                        }
                                    }}
                                    value={companyCodes.length > 0 && getValues('companyCode')}
                                />
                            </div>
                            {errors.companyCode && <p className='error-form'>{errors.companyCode.message}</p>}
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-md-6  mt-3 operatingCounties'>
                            <label className='e-label-select mb-2 py-0 '>Operating Counties *</label>
                            <MultiSelectComponent
                                {...register('operatingCounties', {
                                    required: 'Please choose at least one county',
                                    validate: validateCountySelection,
                                })}
                                dataSource={operatingCountiesData}
                                mode='CheckBox'
                                placeholder='Select a county'
                                change={(e) => {
                                    if (e.isInteracted) {
                                        setValue('operatingCounties', e.value);
                                    }
                                }}
                                fields={{ value: 'countyStateCode', text: 'countyName' }}
                                allowCustomValue
                                showSelectAll={true}
                                selectAllText='Select All'
                                unSelectAllText='Unselect All'
                                value={operatingCountiesData.length > 0 && getValues('operatingCounties')}
                            >
                                <Inject services={[CheckBoxSelection]} />
                            </MultiSelectComponent>
                            {errors.operatingCounties && (
                                <p className='error-form'>{errors.operatingCounties.message}</p>
                            )}
                        </div>
                        <div className='col-md-6  mt-3'>
                            <label className='e-label-select mb-2'>Contiguous Counties</label>
                            <MultiSelectComponent
                                {...register('contiguousCounties', {
                                    validate: validateCountySelection,
                                })}
                                dataSource={contiguousCounties}
                                mode='Default'
                                placeholder='Select a county'
                                change={(e) => {
                                    if (e.isInteracted) {
                                        setValue('contiguousCounties', e.value);
                                    }
                                }}
                                fields={{ value: 'countyStateCode', text: 'countyName' }}
                                value={contiguousCounties.length > 0 && getValues('contiguousCounties')}
                                allowCustomValue
                            />
                        </div>
                    </div>
                </form>
            </DisabledWrapper>
            <p className='error-form'>{overlapError}</p>
        </Row>
    );
};
